import { useMemo, useState } from 'react';
import {
  Link,
  Outlet,
  createFileRoute,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileSpreadsheet, PackageCheck, Plus, Truck } from 'lucide-react';
import { api } from '@/lib/api/client';
import type { PaginatedResponse, PurchaseOrder } from '@/lib/api/types';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
  Toolbar,
} from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup, Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { SearchInput } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel, money, num, qty, smartDate } from '@/lib/format';
import { n, poKeys } from '@/features/buying/api';
import { expectedLabel } from '@/features/buying/dates';
import { channelLabel } from '@/features/buying/channels';
import { totalsByUnit, unitTotalsText } from '@/features/buying/unit-totals';
import {
  PO_STATUS_FILTERS,
  PoStatusBadge,
  VendorSelect,
  useVendorOptions,
} from '@/features/buying/parts';
import type { PoStatus, PoSummary } from '@/features/buying/types';
import { asText } from '@/lib/searchParams';
import { PurchasingTabs } from '@/features/buying/PurchasingTabs';

const PAGE_SIZE = 50;

/** TASK-0241: 'open' is sent + partly received (the orders still coming). */
type StatusFilter = PoStatus | 'all' | 'open';

function isStatus(value: unknown): value is StatusFilter {
  return (
    typeof value === 'string' &&
    (value === 'open' || PO_STATUS_FILTERS.some((option) => option.value === value))
  );
}

/** TASK-0241: where a PO came from, in a word. */
function originWord(po: PurchaseOrder): string {
  const origin = po.origin ?? (po.mintedFromImportId != null ? 'invoice' : null);
  return origin === 'order_guide'
    ? 'Guide'
    : origin === 'invoice'
      ? 'Invoice'
      : 'By hand';
}

/**
 * Every field is optional in the *type* so a `<Link search={{}}>` is legal —
 * under flat file routing the children of `/purchasing` inherit this schema,
 * and a required field here would force every link in the section to restate
 * the whole filter set. Defaults are applied when the screen reads them.
 */
export type PurchasingSearch = {
  id?: number;
  status?: StatusFilter;
  vendorId?: number;
  q?: string;
  page?: number;
};

export const Route = createFileRoute('/_app/purchasing')({
  staticData: { crumb: 'Purchasing' },
  validateSearch: (s: Record<string, unknown>): PurchasingSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    status: isStatus(s.status) ? s.status : 'all',
    vendorId: Number(s.vendorId) > 0 ? Number(s.vendorId) : undefined,
    q: asText(s.q, ''),
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: PurchasingRoute,
});

/**
 * `purchasing.tsx` is the parent of `/purchasing/$id`, `/new`, `/receive` and
 * `/import` under flat file routing, so it has to hand the frame over when a
 * child matches instead of rendering the list above it.
 */
function PurchasingRoute() {
  const children = useChildMatches();
  if (children.length > 0) return <Outlet />;
  return <PurchaseOrderList />;
}

function PurchaseOrderList() {
  const raw = Route.useSearch();
  const search = {
    id: raw.id,
    status: raw.status ?? ('all' as StatusFilter),
    vendorId: raw.vendorId,
    q: raw.q ?? '',
    page: raw.page ?? 1,
  };
  const navigate = useNavigate({ from: Route.fullPath });
  const vendors = useVendorOptions();

  useShortcutHint([
    { keys: 'j / k', label: 'move' },
    { keys: '↵', label: 'inspect' },
  ]);

  const filters = useMemo(
    () => ({
      ...(search.status === 'all'
        ? {}
        : {
            status:
              search.status === 'open' ? 'submitted,partially_received' : search.status,
          }),
      ...(search.vendorId ? { vendorId: search.vendorId } : {}),
      ...(search.q ? { search: search.q } : {}),
    }),
    [search.status, search.vendorId, search.q]
  );

  const list = useQuery({
    queryKey: poKeys.list({ ...filters, page: search.page }),
    queryFn: () =>
      api.get<PaginatedResponse<PurchaseOrder>>('/vendors/purchase-orders', {
        params: { ...filters, page: search.page, pageSize: PAGE_SIZE },
      }),
  });

  /**
   * Filter-wide aggregates. Deliberately a separate call: deriving these from
   * `list.data` would only ever describe the visible page, which is the exact
   * bug this endpoint was added to fix.
   */
  const summary = useQuery({
    queryKey: poKeys.summary(filters),
    queryFn: () =>
      api.get<PoSummary>('/vendors/purchase-orders/summary', { params: filters }),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const counts = summary.data?.statusCounts ?? {};
  const openCount = (counts.submitted ?? 0) + (counts.partially_received ?? 0);

  const setSearch = (next: PurchasingSearch) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  const columns: Column<PurchaseOrder>[] = [
    {
      id: 'id',
      header: 'PO',
      width: '4.5rem',
      mono: true,
      cell: (po) => <span className="text-ink">#{po.id}</span>,
    },
    {
      id: 'vendor',
      header: 'Vendor',
      cell: (po) => (
        <span className="text-ink truncate">{po.vendorName ?? 'Unassigned'}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '9rem',
      cell: (po) => (
        <PoStatusBadge status={po.status} closedShort={po.closedShortAt != null} />
      ),
    },
    {
      id: 'invoice',
      header: 'Invoice',
      width: '9rem',
      mono: true,
      hideBelow: 'lg',
      // TASK-0236: the vendor invoices recorded on the PO ("70185 · $3,412" / "2 invoices");
      // a PO made from an invoice, or an old one, shows the number written on it.
      cell: (po) =>
        (po.invoiceCount ?? 0) > 1 ? (
          <span className="text-ink">{po.invoiceCount} invoices</span>
        ) : po.invoiceCount === 1 ? (
          <span className="text-ink truncate">
            {po.firstInvoiceNumber ?? 'no number'} · {money(po.invoicedTotalCents ?? 0)}
          </span>
        ) : po.invoiceNumber ? (
          <Code>{po.invoiceNumber}</Code>
        ) : po.legacyVtransId ? (
          <Code>{po.legacyVtransId}</Code>
        ) : po.billing === 'not_billed' &&
          (po.status === 'received' || po.status === 'partially_received') ? (
          <span className="text-warn">Not billed</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'origin',
      header: 'Origin',
      width: '5rem',
      hideBelow: 'xl',
      cell: (po) => <span className="text-ink-muted">{originWord(po)}</span>,
    },
    {
      id: 'placedBy',
      header: 'Placed by',
      width: '7rem',
      hideBelow: 'lg',
      cell: (po) =>
        po.placedBy ? (
          <span className="text-ink truncate">{po.placedBy}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'sent',
      header: 'Sent',
      width: '7.5rem',
      hideBelow: 'xl',
      cell: (po) =>
        po.submittedAt ? (
          <span className="truncate" title={channelLabel(po.sentVia) ?? undefined}>
            {smartDate(po.submittedAt)}
            {po.sentVia ? (
              <span className="text-ink-faint"> · {channelLabel(po.sentVia)}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'lines',
      header: 'Lines',
      width: '4rem',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (po) => num(po.itemCount ?? 0),
    },
    {
      id: 'expected',
      header: 'Expected',
      width: '6.5rem',
      mono: true,
      hideBelow: 'lg',
      cell: (po) =>
        po.expectedAt ? (
          <span className="flex flex-col leading-tight">
            <span
              className={po.overdue ? 'text-warn' : undefined}
              title={
                po.overdue ? 'Late' : po.shipTo ? 'Expected at the store' : undefined
              }
            >
              {expectedLabel(po.expectedAt)}
            </span>
            {/* TASK-0256 (DROP-1): the drop-off location it goes through. */}
            {po.shipTo ? (
              <span
                className="text-2xs text-ink-faint truncate font-sans"
                title={`Drops at ${po.shipTo.name}`}
              >
                via {po.shipTo.name}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'updated',
      header: 'Updated',
      width: '6.5rem',
      mono: true,
      hideBelow: 'xl',
      cell: (po) => smartDate(po.updatedAt),
    },
    {
      id: 'total',
      header: 'Total',
      width: '6.5rem',
      align: 'right',
      mono: true,
      cell: (po) =>
        po.totalIsEstimate ? (
          <span
            className="text-ink-muted"
            title="Estimated: no invoice cost yet on some lines"
          >
            est. {money(po.total ?? 0)}
          </span>
        ) : (
          money(po.total ?? 0)
        ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Buying"
        title="Purchasing"
        subtitle="Purchase orders, receiving and invoice capture"
        tabs={<PurchasingTabs />}
        actions={
          <>
            <Button
              variant="default"
              size="sm"
              asChild
              icon={<PackageCheck className="size-3.5" />}
            >
              <Link to="/purchasing/receive" search={{}}>
                Record invoice
              </Link>
            </Button>
            <Button
              variant="primary"
              size="sm"
              asChild
              icon={<Plus className="size-3.5" />}
            >
              <Link to="/purchasing/new" search={{}}>
                New PO
              </Link>
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchInput
          size="sm"
          className="w-64"
          value={search.q}
          onValueChange={(q) => setSearch({ q, page: 1 })}
          placeholder="PO number, invoice, vendor…"
        />
        <VendorSelect
          size="sm"
          className="w-52"
          value={search.vendorId ?? null}
          vendors={vendors.data}
          loading={vendors.isPending}
          includeInactive
          placeholder="All vendors"
          onChange={(vendorId) => setSearch({ vendorId: vendorId ?? undefined, page: 1 })}
        />
        <ChipGroup<StatusFilter>
          className="ml-auto"
          value={search.status}
          onChange={(status) => setSearch({ status, page: 1 })}
          options={[
            ...PO_STATUS_FILTERS.slice(0, 1).map((option) => ({
              value: option.value as StatusFilter,
              label: option.label,
              count: summary.data?.totalCount,
            })),
            // TASK-0241: the orders still coming, in one chip.
            {
              value: 'open' as StatusFilter,
              label: 'Open (sent + partial)',
              count: openCount,
            },
            ...PO_STATUS_FILTERS.slice(1).map((option) => ({
              value: option.value as StatusFilter,
              label: option.label,
              count: counts[option.value] ?? 0,
              ...(option.value === 'partially_received' ? { tone: 'warn' as const } : {}),
            })),
          ]}
        />
      </Toolbar>

      <div className="border-line shrink-0 border-b px-4 py-3">
        <StatRow className="md:grid-cols-3 xl:grid-cols-3">
          <Stat
            label="Purchase orders"
            icon={<Truck />}
            value={summary.isPending ? '—' : num(summary.data?.totalCount ?? 0)}
            sub="matching these filters"
          />
          <Stat
            label="Order value"
            icon={<FileSpreadsheet />}
            value={summary.isPending ? '—' : money(summary.data?.totalValue ?? 0)}
            sub="invoice cost where known, else the estimate"
          />
          <Stat
            label="Awaiting receipt"
            icon={<PackageCheck />}
            tone={openCount > 0 ? 'warn' : undefined}
            value={summary.isPending ? '—' : num(openCount)}
            sub="ordered or partially received"
          />
        </StatRow>
        {summary.isError ? (
          <p className="text-2xs text-negative mt-2">
            Filter-wide totals unavailable — the figures above would only describe this
            page, so they are withheld.
          </p>
        ) : null}
      </div>

      <PageBody bleed className="flex min-h-0">
        <SplitView
          storageKey="purchasing"
          onCloseDetail={() => setSearch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<Truck />}
              title="No purchase order selected"
              description="Pick a row to see its lines, receiving progress and totals."
            />
          }
          detail={search.id ? <PoInspector id={search.id} /> : undefined}
          list={
            <>
              <DataTable
                rows={rows}
                columns={columns}
                rowKey={(po) => po.id}
                loading={list.isPending}
                error={list.error}
                onRetry={() => void list.refetch()}
                isRowActive={(po) => po.id === search.id}
                rowTone={(po) =>
                  po.status === 'partially_received'
                    ? 'warn'
                    : po.status === 'cancelled'
                      ? 'negative'
                      : null
                }
                onRowOpen={(po) => setSearch({ id: po.id })}
                empty={{
                  icon: <Truck />,
                  title: 'No purchase orders',
                  description:
                    search.q || search.status !== 'all' || search.vendorId
                      ? 'No PO matches these filters.'
                      : 'Build one from the vendor catalog, or import a supplier invoice.',
                  action: (
                    <Button size="sm" asChild>
                      <Link to="/purchasing/new" search={{}}>
                        New purchase order
                      </Link>
                    </Button>
                  ),
                }}
                footer={
                  <Pagination
                    className="w-full"
                    page={search.page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    onPageChange={(page) => setSearch({ page })}
                  />
                }
              />
            </>
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

function PoInspector({ id }: { id: number }) {
  const [showAll, setShowAll] = useState(false);

  const detail = useQuery({
    queryKey: poKeys.detail(id),
    queryFn: () => api.get<PurchaseOrder>(`/vendors/purchase-orders/${id}`),
  });

  if (detail.isPending) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="p-3">
        <ErrorState
          title="Could not load this purchase order"
          message={detail.error instanceof Error ? detail.error.message : undefined}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const po = detail.data;
  const items = po.items ?? [];
  // Eaches and pounds are kept apart: each is shown on its own, and the bar is
  // the average of the lines' own progress rather than one sum of mixed units.
  const ordered = totalsByUnit(items, (item) => ({
    unit: item.unit,
    quantity: n(item.orderedQty),
  }));
  const received = totalsByUnit(items, (item) => ({
    unit: item.unit,
    quantity: n(item.receivedQty),
  }));
  const counted = items.filter((item) => n(item.orderedQty) > 0);
  const progress =
    counted.length > 0
      ? (counted.reduce(
          (sum, item) => sum + Math.min(1, n(item.receivedQty) / n(item.orderedQty)),
          0
        ) /
          counted.length) *
        100
      : 0;
  const visible = showAll ? items : items.slice(0, 12);

  return (
    <div className="flex flex-col gap-3 p-3" data-testid="po-inspector">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-ink font-mono text-lg font-medium tabular-nums">
            #{po.id}
          </div>
          <div className="text-ink-muted truncate text-xs">
            {po.vendorName ?? 'Unassigned vendor'}
          </div>
        </div>
        {/* TASK-0251 review m2: a closed-short order reads "Closed short" here too, as in its list row. */}
        <PoStatusBadge status={po.status} closedShort={po.closedShortAt != null} />
      </div>

      <Button size="sm" asChild icon={<ArrowRight className="size-3.5" />}>
        <Link to="/purchasing/$id" params={{ id: String(po.id) }} search={{}}>
          Open full detail
        </Link>
      </Button>

      <Panel title="Receiving">
        <div className="bg-surface-sunken mb-2 h-1.5 w-full overflow-hidden rounded-xs">
          <div
            className={
              progress >= 100
                ? 'bg-positive h-full'
                : progress > 0
                  ? 'bg-warn h-full'
                  : 'bg-line h-full'
            }
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-baseline justify-between font-mono text-xs tabular-nums">
          <span className="text-ink">
            {unitTotalsText(received, '0')}{' '}
            <span className="text-ink-faint">of {unitTotalsText(ordered, '0')}</span>
          </span>
          <span className="text-ink-muted">{progress.toFixed(0)}%</span>
        </div>
      </Panel>

      <Panel title="Order">
        <DetailList columns={2}>
          <Detail label="Order total" mono>
            {money(po.total ?? 0)}
          </Detail>
          <Detail label="Lines" mono>
            {num(items.length)}
          </Detail>
          <Detail label="Expected" mono>
            {expectedLabel(po.expectedAt)}
          </Detail>
          <Detail label="Invoice" mono>
            {po.invoiceNumber ?? '—'}
          </Detail>
          <Detail label="Marked ordered" mono>
            {po.submittedAt ? smartDate(po.submittedAt) : '—'}
          </Detail>
          <Detail label="Received" mono>
            {po.receivedAt ? smartDate(po.receivedAt) : '—'}
          </Detail>
        </DetailList>
        {po.notes ? (
          <p className="border-line-soft text-ink-muted mt-2 border-t pt-2 text-xs leading-relaxed whitespace-pre-wrap">
            {po.notes}
          </p>
        ) : null}
      </Panel>

      <Panel
        title={`Lines · ${countLabel(items.length, 'line')}`}
        bleed
        bodyClassName="divide-y divide-line-soft"
      >
        {items.length === 0 ? (
          <div className="text-ink-faint p-3 text-xs">This PO has no lines.</div>
        ) : (
          <>
            {visible.map((item) => {
              const orderedQty = n(item.orderedQty);
              const receivedQty = n(item.receivedQty);
              const done = receivedQty >= orderedQty && orderedQty > 0;
              return (
                <div key={item.id} className="px-3 py-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-ink min-w-0 flex-1 truncate text-xs">
                      {item.productName}
                    </span>
                    <span className="text-2xs text-ink-muted shrink-0 font-mono tabular-nums">
                      {money(item.lineTotal ?? 0)}
                    </span>
                  </div>
                  <div className="text-2xs text-ink-faint mt-0.5 flex items-center gap-2 font-mono tabular-nums">
                    <span
                      className={
                        done ? 'text-positive' : receivedQty > 0 ? 'text-warn' : ''
                      }
                    >
                      {qty(receivedQty)} / {qty(orderedQty)}
                    </span>
                    <span>{item.unit ?? 'ea'}</span>
                    <span>@ {money(item.unitCost)}</span>
                    {item.expiryRequired ? <Badge tone="warn">expiry</Badge> : null}
                  </div>
                </div>
              );
            })}
            {items.length > visible.length ? (
              <button
                onClick={() => setShowAll(true)}
                className="text-2xs text-signal-ink hover:bg-surface-hover w-full px-3 py-1.5 text-left"
              >
                Show {items.length - visible.length} more lines
              </button>
            ) : null}
          </>
        )}
      </Panel>
    </div>
  );
}
